import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { createAssetClassFilterFields } from "@/features/FilterTable/assetClassFilterFields";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import { z } from "zod";
import { DEFAULT_ASSET_CLASS_OPTIONS } from "../S3StaticData/edit-form-conf";
import { useTranslation } from "react-i18next";

const filterS3TimeSeriesSchema = z.object({
  s3_static_datum_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  isin_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  currency_contains: z.string().optional(),
  date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  asset_class_1_equals: z.string().optional(),
  asset_class_2_equals: z.string().optional(),
});

const filterS3TimeSeriesFields = filterS3TimeSeriesSchema.keyof().enum;

export type FilterS3TimeSeriesSchema = z.infer<typeof filterS3TimeSeriesSchema>;

const assetDepFields: readonly DepField<FilterS3TimeSeriesSchema>[] = [
  {
    name: filterS3TimeSeriesFields.s3_static_datum_name_contains,
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: filterS3TimeSeriesFields.isin_contains,
    key: "isin",
    extract: "text",
    bucket: "equals",
  },
];

export function FilterS3TimeSeries() {
  const { t, i18n } = useTranslation("tables");
  const tableName = Tables.S3_TIME_SERIES;

  const fields: Field[] = [
    {
      name: filterS3TimeSeriesFields.s3_static_datum_name_contains,
      label: t(`${tableName}.searchbox.s3_static_datum_name_contains`, { defaultValue: "Asset" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.S3}/${Tables.S3_STATIC_DATA}`,
      id_column: "s3_static_datum_id",
      column_name: "name",
      col_span: 2,
      filters: makeFiltersBuilder<FilterS3TimeSeriesSchema>(
        assetDepFields,
        filterS3TimeSeriesFields.s3_static_datum_name_contains,
      ),
    },
    ...createAssetClassFilterFields({
      assetClassFieldName: filterS3TimeSeriesFields.asset_class_1_equals,
      assetSubclassFieldName: filterS3TimeSeriesFields.asset_class_2_equals,
      assetClassLabel: t(`${Tables.S3_TIME_SERIES}.filter.asset_class_1`),
      assetSubclassLabel: t(`${Tables.S3_TIME_SERIES}.filter.asset_class_2`),
      client: Clients.S3,
      staticDataTableName: Tables.S3_STATIC_DATA,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      assetSubclassOptions: DEFAULT_ASSET_CLASS_OPTIONS,
    }),
    {
      name: filterS3TimeSeriesFields.isin_contains,
      label: t(`${tableName}.searchbox.isin_contains`, { defaultValue: "ISIN" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.S3}/${Tables.S3_STATIC_DATA}`,
      id_column: "s3_static_datum_id",
      column_name: "isin",
      filters: makeFiltersBuilder<FilterS3TimeSeriesSchema>(
        assetDepFields,
        filterS3TimeSeriesFields.isin_contains,
      ),
    },
    {
      name: filterS3TimeSeriesFields.currency_contains,
      label: t(`${tableName}.searchbox.currency_contains`, { defaultValue: "Currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    {
      name: "date",
      type: "dateRange",
      label: t(`${tableName}.searchbox.date`, { defaultValue: "Value date" }),
      from: { name: filterS3TimeSeriesFields.date_from },
      to: { name: filterS3TimeSeriesFields.date_to },
      col_span: 2,
    },
    {
      name: "create",
      type: "dateRange",
      label: t(`${tableName}.searchbox.create`, { defaultValue: "Creation date" }),
      from: { name: filterS3TimeSeriesFields.create_from },
      to: { name: filterS3TimeSeriesFields.create_to },
      col_span: 2,
    },
  ];

  return (
    <FilterTable
      schema={filterS3TimeSeriesSchema}
      client={Clients.S3}
      tableName={Tables.S3_TIME_SERIES}
      fields={fields}
      columns={2}
    />
  );
}
